import { useEffect, useRef, useState } from "react";
import type { Activity, StageView } from "../hooks/useMigration";
import type { Stage } from "../types";

interface Props {
  stages: StageView[];
  activity: Activity[];
  running: boolean;
}

const GLYPH: Record<string, string> = {
  done: "✓",
  skipped: "–",
  failed: "!",
  running: "",
  pending: "",
};

const MARK: Record<string, string> = {
  created: "+",
  updated: "↻",
  skipped: "=",
  failed: "!",
  "dry-run": "·",
};

/**
 * What each stage is doing, inside the stage it belongs to.
 *
 * The running stage opens itself and stays open, so the detail is there
 * without being asked for. A finished stage collapses to a count, because
 * once it is done the only thing most people want from it is that it is done
 * - and six open logs is not a progress list, it is a wall.
 */
function StageActivity({
  lines,
  open,
  live,
}: {
  lines: Activity[];
  open: boolean;
  live: boolean;
}) {
  const list = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);

  /*
   * Follows the tail, unless someone has scrolled up to read something.
   *
   * A log that yanks itself back to the bottom while you are reading an
   * earlier line is worse than one that does not follow at all.
   */
  useEffect(() => {
    const el = list.current;
    if (!el || !open || !pinned.current) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length, open]);

  if (!open || lines.length === 0) return null;

  return (
    <ol
      className="stage-activity"
      ref={list}
      onScroll={() => {
        const el = list.current;
        if (el) {
          pinned.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }
      }}
    >
      {lines.map((entry) => (
        <li key={entry.id} className={entry.outcome ?? ""}>
          <span className="stage-activity-mark" aria-hidden="true">
            {MARK[entry.outcome ?? ""] ?? "·"}
          </span>
          <span className="stage-activity-body">
            <span>{entry.message}</span>
            {entry.detail && (
              <span className="stage-activity-detail">{entry.detail}</span>
            )}
          </span>
          {entry.total && (
            <span className="stage-activity-count">
              {entry.index}/{entry.total}
            </span>
          )}
        </li>
      ))}
      {live && (
        <li className="stage-activity-working" aria-hidden="true">
          <span className="spin" />
          <span>working{"…"}</span>
        </li>
      )}
    </ol>
  );
}

/**
 * The pipeline, with each stage's state and what it did.
 *
 * Shown for the whole run rather than only while working: afterwards this is
 * the record of what actually happened - which stages ran, which were
 * skipped, and every resource each one touched.
 */
export function StageList({ stages, activity, running }: Props) {
  // Stages the reader has opened or closed by hand. Anything absent follows
  // the default: open while running, closed once finished.
  const [toggled, setToggled] = useState<Partial<Record<Stage, boolean>>>({});

  const byStage = new Map<Stage, Activity[]>();
  for (const entry of activity) {
    const list = byStage.get(entry.stage) ?? [];
    list.push(entry);
    byStage.set(entry.stage, list);
  }

  return (
    <ol className="stages">
      {stages.map((stage) => {
        const s = stage.progress;
        const lines = byStage.get(stage.id) ?? [];
        const isRunning = s.state === "running";
        const open = toggled[stage.id] ?? isRunning;
        const hasLines = lines.length > 0;

        return (
          <li key={stage.id} className={`stage ${s.state} ${open ? "open" : ""}`}>
            <div className="stage-row">
              <span className="stage-mark" aria-hidden="true">
                {isRunning ? <span className="spin" /> : GLYPH[s.state]}
              </span>

              <span className="stage-body">
                <span className="stage-label">{stage.label}</span>
                {(s.message || s.detail) && (
                  <span className="stage-detail">
                    {s.message}
                    {s.detail ? ` · ${s.detail}` : ""}
                  </span>
                )}
              </span>

              {hasLines && (
                <button
                  type="button"
                  className="stage-toggle"
                  aria-expanded={open}
                  onClick={() =>
                    setToggled((t) => ({ ...t, [stage.id]: !open }))
                  }
                >
                  {open ? "Hide" : "Show"} activity
                  <span className="stage-toggle-count">{lines.length}</span>
                </button>
              )}
            </div>

            <StageActivity
              lines={lines}
              open={open && hasLines}
              live={isRunning && running}
            />
          </li>
        );
      })}
      {running && <li className="stage-hint">This takes up to a minute.</li>}
    </ol>
  );
}
